import * as React from 'react';
import type { ISupportNotification } from '../../../models/Support/ISupportNotification';
import { SUPPORT_FAVICON_HREF } from './supportLayoutConstants';

const NOTIFICATION_ICON_SIZE_PX = 192;

export type DesktopNotificationPermission = NotificationPermission | 'unsupported';

export interface SupportDesktopNotificationContent {
	title: string;
	body: string;
	/** Tabs that react to the same rise build the same tag, so the browser shows one popup instead of one per tab. */
	tag: string;
	icon?: string;
	/** Set when the popup stands for one notification; a summary popup opens the bell instead. */
	notification?: ISupportNotification;
}

function getNotificationApi(): typeof Notification | undefined {
	return typeof globalThis.Notification === 'function' ? globalThis.Notification : undefined;
}

export function readDesktopNotificationPermission(): DesktopNotificationPermission {
	return getNotificationApi()?.permission ?? 'unsupported';
}

let supportNotificationIconPromise: Promise<string | undefined> | undefined;

// Chrome can't draw an SVG as a notification icon, so the support favicon goes through a canvas once per page.
export function loadSupportNotificationIcon(): Promise<string | undefined> {
	supportNotificationIconPromise ??= new Promise(resolve => {
		const image = new Image();
		image.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = NOTIFICATION_ICON_SIZE_PX;
			canvas.height = NOTIFICATION_ICON_SIZE_PX;
			const context = canvas.getContext('2d');
			if (!context) {
				resolve(undefined);
				return;
			}
			context.drawImage(image, 0, 0, NOTIFICATION_ICON_SIZE_PX, NOTIFICATION_ICON_SIZE_PX);
			resolve(canvas.toDataURL('image/png'));
		};
		image.onerror = () => resolve(undefined);
		image.src = SUPPORT_FAVICON_HREF;
	});
	return supportNotificationIconPromise;
}

export function resetSupportNotificationIconForTests() {
	supportNotificationIconPromise = undefined;
}

/** Builds the popup for `newCount` new rows from a feed sorted newest first; null when no unread row is left. */
export function buildSupportDesktopNotificationContent(
	feed: ISupportNotification[],
	newCount: number,
	icon: string | undefined
): SupportDesktopNotificationContent | null {
	if (newCount < 1)
		throw new Error(`buildSupportDesktopNotificationContent: newCount must be positive, got ${newCount}`);

	const newest = feed.find(notification => !notification.readDate);
	if (!newest) return null;

	const tag = `support-notification-${newest.id}`;
	if (newCount === 1) {
		return { title: newest.title, body: newest.message ?? '', tag, icon, notification: newest };
	}
	return { title: `${newCount} new support notifications`, body: newest.title, tag, icon };
}

export function showSupportDesktopNotification(content: SupportDesktopNotificationContent, onClick: () => void) {
	const NotificationApi = getNotificationApi();
	if (NotificationApi?.permission !== 'granted') return;

	let popup: Notification;
	try {
		popup = new NotificationApi(content.title, {
			body: content.body,
			tag: content.tag,
			icon: content.icon,
			silent: false,
		});
	} catch {
		// Android Chrome allows notifications only from a service worker; the badge still shows the count.
		return;
	}

	popup.onclick = () => {
		window.focus();
		popup.close();
		onClick();
	};
}

export interface SupportDesktopNotificationsOptions {
	unreadCount: number | undefined;
	loadLatestNotifications(): Promise<ISupportNotification[]>;
	onNotificationClick(notification: ISupportNotification): void;
	onSummaryClick(): void;
}

/** Shows a desktop notification when the unread count rises; the first known count only sets the baseline. */
export function useSupportDesktopNotifications(options: SupportDesktopNotificationsOptions) {
	const { unreadCount } = options;
	const previousCountRef = React.useRef<number | undefined>(undefined);
	const isMountedRef = React.useRef(false);
	// The popup can be clicked long after this render; the ref hands it the current handlers.
	const optionsRef = React.useRef(options);
	optionsRef.current = options;

	React.useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	React.useEffect(() => {
		if (unreadCount === undefined) return;

		const previousCount = previousCountRef.current;
		previousCountRef.current = unreadCount;
		if (previousCount === undefined || unreadCount <= previousCount) return;
		if (readDesktopNotificationPermission() !== 'granted') return;

		const newCount = unreadCount - previousCount;
		Promise.all([optionsRef.current.loadLatestNotifications(), loadSupportNotificationIcon()])
			.then(([feed, icon]) => {
				if (!isMountedRef.current) return;
				const content = buildSupportDesktopNotificationContent(feed, newCount, icon);
				if (!content) return;
				showSupportDesktopNotification(content, () => {
					if (content.notification) optionsRef.current.onNotificationClick(content.notification);
					else optionsRef.current.onSummaryClick();
				});
			})
			.catch(() => {
				// A failed feed load costs one popup; the badge still shows the count.
			});
	}, [unreadCount]);
}

/** Tracks the page's notification permission, including changes made in site settings while the page is open. */
export function useDesktopNotificationPermission() {
	const [permission, setPermission] = React.useState<DesktopNotificationPermission>(readDesktopNotificationPermission);

	React.useEffect(() => {
		let isDisposed = false;
		let status: PermissionStatus | undefined;
		const syncPermission = () => setPermission(readDesktopNotificationPermission());

		globalThis.navigator?.permissions
			?.query({ name: 'notifications' })
			.then(permissionStatus => {
				if (isDisposed) return;
				status = permissionStatus;
				status.addEventListener('change', syncPermission);
				syncPermission();
			})
			.catch(() => {
				// Without the Permissions API, the value only updates after requestPermission.
			});

		return () => {
			isDisposed = true;
			status?.removeEventListener('change', syncPermission);
		};
	}, []);

	const requestPermission = async () => {
		const NotificationApi = getNotificationApi();
		if (!NotificationApi) return;
		try {
			await NotificationApi.requestPermission();
		} catch {
			// The read below reports whatever state the browser settled on.
		}
		setPermission(readDesktopNotificationPermission());
	};

	return { permission, requestPermission };
}
